import { getEnvConfig, PSConfig } from "../hooks/useEnv";
import {
  areCookiesSupported,
  calculateExpirationDate,
  deleteCookie,
  getCookie,
  setSecureCookie,
  validateCookieSize,
} from "../utils/secureCookies";

import { UserProfile, UserRole, userService } from "./userService";

export interface AuthTokens {
  access_token: string;
  id_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
  expires_at: number;
}

interface TokenRefreshConfig {
  refreshThreshold: number; // Seconds before expiration to refresh
  maxRetries: number;
  retryDelay: number; // Milliseconds
}

// Singleton state variables with hybrid storage
let config: PSConfig;
let tokens: AuthTokens | null = null;

// Storage configuration
const STORAGE_CONFIG = {
  cookieName: "ps_auth",
  localStorageKey: "ps_auth",
  useCookies: true, // Primary storage method
  useLocalStorageFallback: true, // Fallback if cookies fail
};

// Token refresh properties (merged from tokenRefresh.service.ts)
let refreshTimer: NodeJS.Timeout | null = null;
let isRefreshing: boolean = false;
const refreshConfig: TokenRefreshConfig = {
  refreshThreshold: 300, // 5 minutes before expiration
  maxRetries: 3,
  retryDelay: 5000, // 5 seconds
};

// Session management
let checkSessionInterval: number | null = null;

// Initialize the auth service
const initializeAuthService = (): void => {
  // Initialize with environment config using useEnv utility
  const envConfig = getEnvConfig();
  if (envConfig) {
    config = envConfig;
  } else {
    // Fallback to minimal config if loading fails
    config = {
      oidc: {
        accessTokenExpiringNotificationTime: 60,
        authority: "https://login.perfectserve.com",
        checkSessionInterval: 60000,
        client_id: "PS-App-Implicit",
        clock_skew: 300,
        post_logout_redirect_uri: "logout/callback",
        redirect_uri: "index.html",
        response_type: "id_token token",
        scope: "openid perfectserve ps-api api-gateway-ps",
        silent_redirect_uri: "silent-renew.html",
        silentRequestTimeout: 15000,
        log: false,
      },
    } as PSConfig;
  }

  // Try to load the global config immediately, then with a delay as fallback
  initializeWithGlobalConfig();

  // Fallback attempt with delay in case global config loads later
  setTimeout(() => {
    initializeWithGlobalConfig();
  }, 100);
};

const initializeWithGlobalConfig = (): void => {
  try {
    // Get config using useEnv utility
    const envConfig = getEnvConfig();
    if (envConfig && envConfig.oidc) {
      config = envConfig;
    }

    initializeFromStorage();
    startSessionCheck();
    startTokenRefreshTimer(); // Start automatic token refresh
  } catch {
    // Silently continue with existing config
  }
};

const initializeFromStorage = (): void => {
  // Try to load from cookies first, then localStorage as fallback
  let storedTokens: string | null = null;
  let storageMethod = "none";

  // Primary: Try cookies
  if (STORAGE_CONFIG.useCookies && areCookiesSupported()) {
    storedTokens = getCookie(STORAGE_CONFIG.cookieName);
    if (storedTokens) {
      storageMethod = "cookies";
    }
  }

  // Fallback: Try localStorage
  if (!storedTokens && STORAGE_CONFIG.useLocalStorageFallback) {
    storedTokens = localStorage.getItem(STORAGE_CONFIG.localStorageKey);
    if (storedTokens) {
      storageMethod = "localStorage";
    }
  }

  if (storedTokens) {
    try {
      tokens = JSON.parse(storedTokens);
      if (tokens && tokens.expires_at < Date.now()) {
        clearAllAuthState();
        return;
      }
    } catch (error) {
      console.error(`Failed to parse tokens from ${storageMethod}:`, error);
      clearAllAuthState();
    }
  }
};

// ============================================================================
// OIDC AUTHENTICATION FLOW
// ============================================================================

/**
 * Helper function to construct redirect URI
 * Handles both full URLs and relative paths
 * @param configuredUri - The configured URI (could be full URL or path)
 * @returns Absolute URL using current origin
 */
const buildRedirectUri = (configuredUri: string | undefined): string => {
  if (!configuredUri) {
    return window.location.origin;
  }

  try {
    // If it's a full URL, extract just the pathname
    const url = new URL(configuredUri);
    return `${window.location.origin}${url.pathname}`;
  } catch {
    // Not a full URL, treat as relative path
    const cleanPath = configuredUri.startsWith("/") ? configuredUri : `/${configuredUri}`;
    return `${window.location.origin}${cleanPath}`;
  }
};

const login = async (): Promise<void> => {
  const state = generateRandomString(32);
  const nonce = generateRandomString(32);

  // Store state and nonce for validation
  sessionStorage.setItem("psx_auth_state", state);
  sessionStorage.setItem("psx_auth_nonce", nonce);

  const params = new URLSearchParams({
    client_id: config.oidc?.client_id || "PS-App-Implicit",
    response_type: config.oidc?.response_type || "id_token token",
    scope: config.oidc?.scope || "openid profile email",
    redirect_uri: buildRedirectUri(config.oidc?.redirect_uri),
    state: state,
    nonce: nonce,
  });

  const authUrl = `${config.oidc?.authority || "https://login.perfectserve.com"}/connect/authorize?${params.toString()}`;
  window.location.href = authUrl;
};

const handleCallback = async (): Promise<boolean> => {
  try {
    const urlParams = new URLSearchParams(window.location.hash.substring(1));
    const state = urlParams.get("state");
    const accessToken = urlParams.get("access_token");
    const idToken = urlParams.get("id_token");
    const tokenType = urlParams.get("token_type");
    const expiresIn = urlParams.get("expires_in");
    const scope = urlParams.get("scope");

    // Validate state
    const storedState = sessionStorage.getItem("psx_auth_state");
    if (state !== storedState) {
      // Clear the URL hash and return false
      window.history.replaceState({}, document.title, window.location.pathname);
      return false;
    }

    // Clean up stored state
    sessionStorage.removeItem("psx_auth_state");
    sessionStorage.removeItem("psx_auth_nonce");

    if (accessToken && idToken && expiresIn) {
      // Store tokens
      tokens = {
        access_token: accessToken,
        id_token: idToken,
        token_type: tokenType || "Bearer",
        expires_in: parseInt(expiresIn),
        scope: scope || "",
        expires_at: Date.now() + parseInt(expiresIn) * 1000,
      };

      storeTokens();

      // Token is now managed by HttpProvider via getAuthToken callback
      // No manual notification needed

      // User profile will be managed by UserContext

      // Start token refresh monitoring
      scheduleNextRefresh();

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);

      return true;
    }

    return false;
  } catch {
    // Handle any errors gracefully without throwing
    window.history.replaceState({}, document.title, window.location.pathname);
    clearAllAuthState();
    return false;
  }
};

const logout = (): void => {
  // Clear all auth state first
  clearAllAuthState();

  // Build logout URL with proper OIDC parameters
  const authority = config.oidc?.authority || "https://login.perfectserve.com";
  const actualRedirectUri = buildRedirectUri(config.oidc?.post_logout_redirect_uri || "logout/callback");

  // Build logout URL with required parameters
  const params = new URLSearchParams({
    post_logout_redirect_uri: actualRedirectUri,
  });

  // Add id_token_hint if available (helps with logout flow)
  if (tokens?.id_token) {
    params.append("id_token_hint", tokens.id_token);
  }

  const logoutUrl = `${authority}/connect/endsession?${params.toString()}`;

  // Store the actual redirect URI for the helper page
  localStorage.setItem("ps_logout_redirect_uri", actualRedirectUri);
  localStorage.setItem("ps_logout_timestamp", Date.now().toString());

  // Direct redirect to external logout
  window.location.href = logoutUrl;
};

const _silentLogout = async (): Promise<void> => {
  try {
    // Build logout URL
    const authority = config.oidc?.authority || "https://login.perfectserve.com";
    const params = new URLSearchParams();

    // Add id_token_hint if available
    if (tokens?.id_token) {
      params.append("id_token_hint", tokens.id_token);
    }

    const logoutUrl = `${authority}/connect/endsession?${params.toString()}`;

    // Make silent logout call in background
    await fetch(logoutUrl, {
      method: "GET",
      mode: "no-cors", // Avoid CORS issues
      credentials: "include", // Include cookies for session termination
    }).catch(() => {
      // Ignore fetch errors - we're logging out anyway
    });
  } catch (_error) {
    // Ignore errors - we're logging out anyway
  } finally {
    // Always clear local state and redirect regardless of external call success
    clearAllAuthState();
    window.location.href = `${window.location.protocol}//${window.location.host}/`;
  }
};

// Option 1: Simple local logout (cleanest)
const logoutLocal = (): void => {
  // Clear all auth state first
  clearAllAuthState();

  // Direct redirect to landing page
  window.location.href = `${window.location.protocol}//${window.location.host}/`;
};

// Alternative logout method that calls external service
const logoutWithExternalCall = (): void => {
  // Clear all auth state first
  clearAllAuthState();

  // Build logout URL with proper OIDC parameters
  const authority = config.oidc?.authority || "https://login.perfectserve.com";
  const postLogoutRedirectUri = buildRedirectUri(config.oidc?.post_logout_redirect_uri || "logout/callback");

  // Build logout URL with required parameters
  const params = new URLSearchParams({
    post_logout_redirect_uri: postLogoutRedirectUri,
  });

  // Add id_token_hint if available (helps with logout flow)
  if (tokens?.id_token) {
    params.append("id_token_hint", tokens.id_token);
  }

  const logoutUrl = `${authority}/connect/endsession?${params.toString()}`;

  // Store redirect info for potential manual handling
  sessionStorage.setItem("ps_logout_redirect", postLogoutRedirectUri);
  sessionStorage.setItem("ps_logout_timestamp", Date.now().toString());

  // Direct redirect to external logout
  window.location.href = logoutUrl;
};

const clearAllAuthState = (): void => {
  // Clear tokens and user profile
  tokens = null;
  userService.clearProfile();

  // Clear both storage methods
  // 1. Clear cookies
  if (STORAGE_CONFIG.useCookies) {
    const cookieDeleted = deleteCookie(STORAGE_CONFIG.cookieName);
    if (cookieDeleted) {
      console.info("Auth tokens cleared from cookies");
    }
  }

  // 2. Clear localStorage
  if (STORAGE_CONFIG.useLocalStorageFallback) {
    try {
      localStorage.removeItem(STORAGE_CONFIG.localStorageKey);
      localStorage.removeItem("ps_userConfiguration");
      console.info("Auth tokens cleared from localStorage");
    } catch (error) {
      console.error("Failed to clear localStorage:", error);
    }
  }

  // Clear timers
  stopTokenRefreshTimer();
  if (checkSessionInterval) {
    clearInterval(checkSessionInterval);
    checkSessionInterval = null;
  }

  // Token removal handled by HttpProvider via getAuthToken callback
  // No manual notification needed

  // Clear any other potential auth-related localStorage items (but NOT sessionStorage)
  if (STORAGE_CONFIG.useLocalStorageFallback) {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("ps_")) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.error("Failed to clear additional localStorage items:", error);
    }
  }

  // DO NOT clear sessionStorage here - it contains state/nonce for OIDC validation
};

// ============================================================================
// TOKEN MANAGEMENT & REFRESH (Merged from tokenRefresh.service.ts)
// ============================================================================

/**
 * Store tokens using hybrid approach: cookies first, localStorage as fallback
 */
const storeTokens = (): void => {
  if (!tokens) return;

  const tokenString = JSON.stringify(tokens);
  let cookieSuccess = false;
  let localStorageSuccess = false;

  // Primary: Try to store in cookies
  if (STORAGE_CONFIG.useCookies && areCookiesSupported()) {
    // Validate cookie size first
    if (validateCookieSize(STORAGE_CONFIG.cookieName, tokenString)) {
      const expirationDate = calculateExpirationDate(tokens.expires_in);

      cookieSuccess = setSecureCookie(STORAGE_CONFIG.cookieName, tokenString, {
        expires: expirationDate,
        secure: window.location.protocol === "https:",
        sameSite: "Strict",
        path: "/",
      });

      if (cookieSuccess) {
        console.info("Auth tokens stored in secure cookies");

        // If cookies work, optionally clear localStorage to migrate
        if (STORAGE_CONFIG.useLocalStorageFallback) {
          try {
            localStorage.removeItem(STORAGE_CONFIG.localStorageKey);
          } catch {
            // Ignore localStorage errors when cleaning up
          }
        }
      }
    } else {
      console.warn("Token too large for cookie storage, falling back to localStorage");
    }
  }

  // Fallback: Store in localStorage if cookies failed or are disabled
  if (!cookieSuccess && STORAGE_CONFIG.useLocalStorageFallback) {
    try {
      localStorage.setItem(STORAGE_CONFIG.localStorageKey, tokenString);
      localStorageSuccess = true;
      console.info("Auth tokens stored in localStorage (fallback)");
    } catch (error) {
      console.error("Failed to store tokens in localStorage:", error);
    }
  }

  // Log storage status
  if (!cookieSuccess && !localStorageSuccess) {
    console.error("Failed to store auth tokens in any storage method");
  }
};

/**
 * Start automatic token refresh monitoring
 */
const startTokenRefreshTimer = (): void => {
  scheduleNextRefresh();

  // Listen for token changes in localStorage
  window.addEventListener("storage", (event) => {
    if (event.key === "ps_auth") {
      scheduleNextRefresh();
    }
  });
};

/**
 * Stop automatic token refresh monitoring
 */
const stopTokenRefreshTimer = (): void => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

/**
 * Schedule the next token refresh based on current token expiration
 */
const scheduleNextRefresh = (): void => {
  // Clear existing timer
  stopTokenRefreshTimer();

  if (!tokens || !tokens.expires_at) {
    return;
  }

  const now = Date.now();
  const expiresAt = tokens.expires_at;
  const refreshAt = expiresAt - refreshConfig.refreshThreshold * 1000;
  const timeUntilRefresh = refreshAt - now;

  // If token is already expired or needs immediate refresh
  if (timeUntilRefresh <= 0) {
    performTokenRefresh();
    return;
  }

  // Schedule the refresh
  refreshTimer = setTimeout(() => {
    performTokenRefresh();
  }, timeUntilRefresh);
};

/**
 * Perform the actual token refresh
 */
const performTokenRefresh = async (retryCount: number = 0): Promise<void> => {
  if (isRefreshing) {
    return;
  }

  isRefreshing = true;

  try {
    const _beforeExpiry = tokens?.expires_at;
    await refreshToken();
    const _afterExpiry = tokens?.expires_at;

    scheduleNextRefresh();
  } catch (_error) {
    // Retry if we haven't exceeded max retries
    if (retryCount < refreshConfig.maxRetries - 1) {
      setTimeout(() => {
        performTokenRefresh(retryCount + 1);
      }, refreshConfig.retryDelay);
    } else {
      handleRefreshFailure();
    }
  } finally {
    isRefreshing = false;
  }
};

/**
 * Handle token refresh failure after all retries
 */
const handleRefreshFailure = (): void => {
  clearAllAuthState();

  // REMOVED: Full page reload redirect
  // Let React Router handle navigation to login page
};

/**
 * Refresh tokens using silent renewal
 */
const refreshToken = async (): Promise<void> => {
  if (!tokens) {
    throw new Error("No tokens available for refresh");
  }

  if (!config.oidc?.authority) {
    throw new Error("OIDC authority not configured");
  }

  // Use oidc-client library for proper silent refresh
  try {
    // Import oidc-client dynamically
    const { UserManager } = await import("oidc-client");

    // Configure UserManager with current settings
    const userManager = new UserManager({
      authority: config.oidc.authority,
      client_id: config.oidc.client_id || "PS-App-Implicit",
      redirect_uri: buildRedirectUri(config.oidc.redirect_uri),
      silent_redirect_uri: buildRedirectUri(config.oidc.silent_redirect_uri || "silent-renew.html"),
      response_type: config.oidc.response_type || "id_token token",
      scope: config.oidc.scope || "openid profile email",
      post_logout_redirect_uri: buildRedirectUri(config.oidc.post_logout_redirect_uri || "logout/callback"),
      accessTokenExpiringNotificationTime: config.oidc.accessTokenExpiringNotificationTime || 60,
      silentRequestTimeout: config.oidc.silentRequestTimeout || 10000,
      automaticSilentRenew: false, // We'll handle this manually
      checkSessionInterval: config.oidc.checkSessionInterval || 2000,
    });

    // Perform silent refresh using oidc-client
    const user = await userManager.signinSilent();

    if (user && user.access_token && user.id_token) {
      // Update our tokens with the new ones from oidc-client
      tokens = {
        access_token: user.access_token,
        id_token: user.id_token,
        token_type: user.token_type || "Bearer",
        expires_in: user.expires_in || 3600,
        scope: user.scope || "",
        expires_at: user.expires_at ? user.expires_at * 1000 : Date.now() + (user.expires_in || 3600) * 1000,
      };

      storeTokens();
    } else {
      throw new Error("Invalid user object returned from oidc-client");
    }
  } catch (_error) {
    // If oidc-client fails, fall back to manual approach
    return new Promise((resolve, reject) => {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";

      // Build the OIDC authorize URL for silent refresh
      const params = new URLSearchParams({
        client_id: config.oidc?.client_id || "PS-App-Implicit",
        response_type: config.oidc?.response_type || "id_token token",
        scope: config.oidc?.scope || "openid profile email",
        redirect_uri: buildRedirectUri(config.oidc?.silent_redirect_uri || "silent-renew.html"),
        prompt: "none", // Critical for silent refresh
        state: `silent_${Date.now()}`, // Unique state for this request
        nonce: `nonce_${Date.now()}`, // Unique nonce for this request
      });

      const silentRefreshUrl = `${config.oidc?.authority}/connect/authorize?${params.toString()}`;
      iframe.src = silentRefreshUrl;

      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === "silent_renew_success") {
          const newTokens = event.data.tokens;
          tokens = {
            ...newTokens,
            expires_at: Date.now() + newTokens.expires_in * 1000,
          };
          storeTokens();

          window.removeEventListener("message", messageHandler);
          document.body.removeChild(iframe);
          resolve();
        } else if (event.data.type === "silent_renew_error") {
          window.removeEventListener("message", messageHandler);
          document.body.removeChild(iframe);
          reject(new Error(event.data.error));
        }
      };

      window.addEventListener("message", messageHandler);
      document.body.appendChild(iframe);

      // Timeout after silentRequestTimeout
      setTimeout(() => {
        window.removeEventListener("message", messageHandler);
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        reject(new Error("Silent token refresh timeout"));
      }, config.oidc?.silentRequestTimeout || 10000);
    });
  }
};

/**
 * Manual trigger for token refresh
 */
const manualRefresh = async (): Promise<void> => {
  await performTokenRefresh();
};

// ============================================================================
// SESSION MANAGEMENT
// ============================================================================

const startSessionCheck = (): void => {
  if (checkSessionInterval) {
    clearInterval(checkSessionInterval);
  }

  if (!config?.oidc?.checkSessionInterval) {
    return;
  }

  checkSessionInterval = window.setInterval(() => {
    checkSession();
  }, config.oidc.checkSessionInterval);
};

const checkSession = async (): Promise<void> => {
  if (!tokens) return;

  // Check if token is about to expire (within accessTokenExpiringNotificationTime)
  const timeUntilExpiry = tokens.expires_at - Date.now();
  const notificationTime = (config.oidc?.accessTokenExpiringNotificationTime || 60) * 1000;

  if (timeUntilExpiry <= notificationTime) {
    try {
      await refreshToken();
    } catch {
      logout();
    }
  }
};

// ============================================================================
// PUBLIC API
// ============================================================================

const isAuthenticated = (): boolean => {
  return tokens !== null && tokens.expires_at > Date.now();
};

const getAccessToken = (): string | null => {
  return tokens?.access_token || null;
};

/**
 * Get the current tokens object (for internal use by other services)
 */
const getTokens = (): AuthTokens | null => {
  return tokens;
};

/**
 * Check if token is near expiration
 */
const isTokenNearExpiration = (): boolean => {
  if (!tokens || !tokens.expires_at) return false;

  const now = Date.now();
  const expiresAt = tokens.expires_at;
  const refreshAt = expiresAt - refreshConfig.refreshThreshold * 1000;

  return now >= refreshAt;
};

/**
 * Get time until token expires (in seconds)
 */
const getTimeUntilExpiration = (): number => {
  if (!tokens || !tokens.expires_at) return 0;

  const now = Date.now();
  const expiresAt = tokens.expires_at;

  return Math.max(0, Math.round((expiresAt - now) / 1000));
};

/**
 * Update refresh configuration
 */
const updateRefreshConfig = (newConfig: Partial<TokenRefreshConfig>): void => {
  Object.assign(refreshConfig, newConfig);
  scheduleNextRefresh();
};

// ============================================================================
// UTILITY METHODS
// ============================================================================

const generateRandomString = (length: number): string => {
  const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return result;
};

// ============================================================================
// INITIALIZATION AND EXPORT
// ============================================================================

// Initialize the auth service immediately
initializeAuthService();

// Export singleton instance with functional API (keeping same interface)
export const authService = {
  // Authentication flow
  login,
  handleCallback,
  logout,
  logoutLocal,
  logoutWithExternalCall,

  // Token management
  isAuthenticated,
  getAccessToken,
  getTokens,
  isTokenNearExpiration,
  getTimeUntilExpiration,

  // Token refresh
  manualRefresh,
  startTokenRefreshTimer,
  stopTokenRefreshTimer,
  updateRefreshConfig,

  // State management
  clearAllAuthState,
};

// Re-export types from UserService for compatibility
export type { UserProfile, UserRole };
